import {
  getKeptFiles,
  getRemovedFiles,
  getRemovedFilesIds,
} from '@risksmart-app/components/src/file/fileUtils';
import { retryFileOperation } from '@risksmart-app/components/src/file/retryFileOperation';
import type {
  UseChangeRequestFileUpdateOptions,
  UseChangeRequestMultiParentFileUpdateOptions,
  UseFileUpdateOptions,
  UseMultiParentFileUpdateOptions,
} from '@risksmart-app/components/src/file/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTRPC } from '@/utils/trpc';

import { useFileUploadTRPC } from './useFileUploadTRPC';

/**
 * tRPC twin of `@risksmart-app/components/src/file/useFileUpdate` (V2:
 * REST upload + Apollo deleteRelationFileById). Same removed-file diffing,
 * same option shapes, same `{ updateFiles, error }` returns.
 *
 * Consumers must import the flag-gated wrappers in `./useFileUpdate`,
 * never these.
 */
const useUpdateFilesTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const uploadFiles = useFileUploadTRPC();
  const deleteRelationFilesMutation = useMutation({
    ...trpc.frontend.files.deleteRelationFiles.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.files.getById.queryKey(),
        });
      },
    }),
  });

  return async ({
    parentType,
    parentIds,
    newFiles,
    originalFiles,
    selectedFiles,
  }: UseMultiParentFileUpdateOptions & {
    newFiles: File[] | undefined;
  }): Promise<void> => {
    const removedFileIds = getRemovedFilesIds(
      originalFiles?.map((f) => ({
        Id: f != null && !(f instanceof File) ? f.Id : undefined,
      })) ?? [],
      getKeptFiles(selectedFiles)
    );

    await uploadFiles({ parentType, parentIds, newFiles });

    if (removedFileIds.length > 0) {
      await retryFileOperation(() =>
        deleteRelationFilesMutation.mutateAsync({
          ParentIds: parentIds,
          FileIds: removedFileIds,
        })
      );
    }
  };
};

export const useFileUpdateTRPC = () => {
  const updateFiles = useUpdateFilesTRPC();

  return {
    updateFiles: async ({
      parentType,
      parentId,
      originalFiles,
      selectedFiles,
    }: UseFileUpdateOptions): Promise<void> =>
      await updateFiles({
        parentType,
        parentIds: [parentId],
        newFiles: selectedFiles?.filter((f): f is File => f instanceof File),
        originalFiles,
        selectedFiles,
      }),
    error: '',
  };
};

const useChangeRequestUpdateFilesTRPC = () => {
  const uploadFiles = useFileUploadTRPC();

  return async ({
    parentType,
    parentIds,
    newFiles,
    originalFiles,
    selectedFiles,
  }: UseChangeRequestMultiParentFileUpdateOptions & {
    newFiles: File[] | undefined;
  }): Promise<void> => {
    const removedFiles = getRemovedFiles(
      originalFiles,
      getKeptFiles(selectedFiles)
    );

    await uploadFiles({
      parentType,
      parentIds,
      newFiles,
      removedFiles,
      isChangeRequest: true,
    });
  };
};

export const useChangeRequestFileUpdateTRPC = () => {
  const updateFiles = useChangeRequestUpdateFilesTRPC();

  return {
    updateFiles: async ({
      parentType,
      parentId,
      originalFiles,
      selectedFiles,
    }: UseChangeRequestFileUpdateOptions): Promise<void> =>
      await updateFiles({
        parentType,
        parentIds: [parentId],
        newFiles: selectedFiles?.filter(
          (selectedFile): selectedFile is File => selectedFile instanceof File
        ),
        originalFiles,
        selectedFiles,
      }),
    error: '',
  };
};

export const useMultiParentFileUpdateTRPC = () => {
  const updateFiles = useUpdateFilesTRPC();

  return {
    updateFiles: async ({
      parentType,
      parentIds,
      originalFiles,
      selectedFiles,
    }: UseMultiParentFileUpdateOptions): Promise<void> =>
      await updateFiles({
        parentType,
        parentIds,
        newFiles: selectedFiles?.filter((f): f is File => f instanceof File),
        originalFiles,
        selectedFiles,
      }),
    error: '',
  };
};
